import { useState } from 'react';
import { vehicleColor, t, type MessageKey } from '@aquabox/shared';
import { useMe } from '../api/queries';
import { useToast } from '../components/Toast';
import { Badge } from '../components/Pills';
import {
  usePanelAssign,
  useSaveDriverMap,
  type PanelAssignLabel,
  type PanelSource,
  type OrderReason,
} from '../api/panel-assign';
import { ShadowAssignCard } from './ShadowAssignCard';

// ── Sekme sabitleri ────────────────────────────────────────────────────────
// Türkçe etiketler burada değil — `panelAssign.tabAbant`/`panelAssign.tabHayat`
// anahtarları i18n'den çekilir. Tür değerleri `MessageKey` olarak işaretlendi
// ki `t()`'ye string geçmek zorunda kalmayalım.
const SOURCES: { value: PanelSource; label: MessageKey }[] = [
  { value: 'ABANT_PANEL', label: 'panelAssign.tabAbant' },
  { value: 'HAYAT_PANEL', label: 'panelAssign.tabHayat' },
];

// ── Reason çevirisi ────────────────────────────────────────────────────────
// `t(\`panelAssign.reason.${string}\`)` template literal'i TypeScript'te
// `MessageKey` ile uyuşmaz (template literal expansion → `string`). Kodları
// burada sabit haritaya bağlayıp `t(REASON_KEYS[k])` çağrısıyla tür güvenliği
// sağlıyoruz. Tüm reason kodları için i18n anahtarı zaten `tr.ts`'te var.
const REASON_KEYS: Record<OrderReason, MessageKey> = {
  NO_LABEL: 'panelAssign.reason.NO_LABEL',
  UNMAPPED: 'panelAssign.reason.UNMAPPED',
  NO_VEHICLE: 'panelAssign.reason.NO_VEHICLE',
  DRIVER_INACTIVE: 'panelAssign.reason.DRIVER_INACTIVE',
  VEHICLE_BUSY: 'panelAssign.reason.VEHICLE_BUSY',
};

function reasonLabel(reason: OrderReason): string {
  return t(REASON_KEYS[reason]);
}

// ── Yardımcılar ───────────────────────────────────────────────────────────

/** "SS:dd" — bugünden eskiyse "GG.AA.YYYY SS:dd". */
function timeLabel(iso: string): { text: string; stale: boolean } {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const oneDayMs = 86_400_000;
  const sameDay = diffMs >= 0 && diffMs < oneDayMs;

  if (sameDay) {
    const text = new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    return { text, stale: false };
  }

  const text = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return { text, stale: true };
}

// ── Plaka rozeti ─────────────────────────────────────────────────────────

function PlateBadge({ plate }: { plate: string }) {
  const bg = vehicleColor(plate);
  return (
    <span
      data-testid="plate-badge"
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 'var(--r-pill)',
        background: bg,
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '0.01em',
      }}
    >
      {plate}
    </span>
  );
}

/**
 * Hayat etiketinin durumu (salt okunur), API `via` alanından: PLATE → "etiket = plaka";
 * ALIAS → "geçiş takma adı → <plaka>"; null (çözülemedi) → "plaka değil".
 */
function HayatLabelStatus({ label }: { label: PanelAssignLabel }) {
  if (label.via === 'PLATE' && label.target) {
    return (
      <span
        data-testid={`hayat-plate-${label.label}`}
        style={{ display: 'flex', gap: 6, alignItems: 'center' }}
      >
        <PlateBadge plate={label.target.plate} />
        <Badge tone="green">{t('panelAssign.viaPlate')}</Badge>
      </span>
    );
  }
  if (label.via === 'ALIAS' && label.target) {
    return (
      <span data-testid={`hayat-alias-${label.label}`}>
        <Badge tone="blue">{t('panelAssign.aliasBadge', { plate: label.target.plate })}</Badge>
      </span>
    );
  }
  return (
    <span data-testid={`hayat-not-plate-${label.label}`}>
      <Badge tone="red">{t('panelAssign.notPlate')}</Badge>
    </span>
  );
}

// ── GET hata kutusu ───────────────────────────────────────────────────────
// Brifing ret2 bulgu 2: "Liste yüklenemedi" kalıcı olarak görünmeli,
// "Bekleyen sipariş yok" mesajı ise GÖRÜNMEYECEK. Toast tek başına yetmez
// çünkü 3 saniye sonra kayboluyor; test de bunu yakalayamazdı.
function LoadErrorBox({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      data-testid="load-error"
      style={{
        padding: '10px 12px',
        background: 'var(--surface)',
        border: '1px solid var(--danger)',
        borderRadius: 'var(--r-row)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--danger)' }}>{t('panelAssign.loadError')}</span>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ fontSize: 12, padding: '4px 10px' }}
        onClick={onRetry}
        data-testid="retry"
      >
        {t('panelAssign.retry')}
      </button>
    </div>
  );
}

// ── Ana ekran ─────────────────────────────────────────────────────────────

/**
 * Panel ataması (A1). Atamayı MOTOR yapar (sipariş geldiğinde ve yeniden dağıtımda); bu ekran
 * yalnız eşlemeyi gösterir/kaydeder: Abant "etiket → şoför" (OWNER yazar) ve Hayat "etiket = plaka"
 * (salt okunur). "Uygula" düğmesi yoktur.
 */
export function PanelAssign() {
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';

  const [source, setSource] = useState<PanelSource>('ABANT_PANEL');

  // Bölüm state'leri — sekme değişince sıfırlanır
  const [pendingMap, setPendingMap] = useState<Record<string, string | null>>({});
  const [rematchFailed, setRematchFailed] = useState(false);

  const toast = useToast();

  const data = usePanelAssign(source);
  const saveMap = useSaveDriverMap();

  // Sekme değişince tüm state'i sıfırla — brifing ret2 bulgu 1
  function handleSourceChange(next: PanelSource) {
    if (next === source) return;
    setSource(next);
    setPendingMap({});
    setRematchFailed(false);
  }

  const labels = data.data?.labels ?? [];
  // Siparişteki ham panel etiketi (çalışan no) gösterilmez; eşlenmiş personel adı gösterilir.
  const personOf = (label: string | null): string => {
    const l = labels.find((x) => x.label === label);
    return l?.driver?.name ?? l?.displayName ?? '';
  };
  const orders = data.data?.orders ?? [];
  const drivers = data.data?.drivers ?? [];

  function handleLabelDriverChange(label: string, driverId: string | null) {
    setPendingMap((prev) => ({ ...prev, [label]: driverId }));
  }

  async function handleSaveMap() {
    try {
      const result = await saveMap.mutateAsync({ source, map: mapToSend });
      setPendingMap({});
      setRematchFailed(result.rematchFailed);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? t('error.internal');
      toast(msg, 'danger');
    }
  }

  // Yalnız pendingMap'teki mevcut eşlemeden FARKLI değerleri say
  const changedEntries = Object.entries(pendingMap).filter(([label, driverId]) => {
    const current = data.data?.labels.find((l) => l.label === label)?.driver?.id ?? null;
    return current !== driverId;
  });
  const hasMapChanges = changedEntries.length > 0;
  const mapToSend = Object.fromEntries(changedEntries);

  return (
    <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <p
        data-testid="panel-assign-intro"
        style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}
      >
        {t('panelAssign.intro')}
      </p>

      {/* Gölge atama: panelin atadığı araç ↔ bölge kuralı (salt okunur; OWNER/MANAGER) */}
      <ShadowAssignCard />

      {/* Sekmeler */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--border)',
          paddingBottom: 0,
        }}
      >
        {SOURCES.map((s) => (
          <button
            key={s.value}
            type="button"
            data-testid={`panel-assign-tab-${s.value}`}
            onClick={() => handleSourceChange(s.value)}
            style={{
              padding: '8px 16px',
              background: 'none',
              border: 'none',
              borderBottom:
                source === s.value ? '2px solid var(--primary)' : '2px solid transparent',
              color: source === s.value ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: source === s.value ? 700 : 400,
              cursor: 'pointer',
              fontSize: 14,
              marginBottom: -1,
            }}
          >
            {t(s.label)}
          </button>
        ))}
      </div>

      {rematchFailed ? (
        <div
          role="alert"
          data-testid="rematch-failed"
          style={{ fontSize: 13, color: 'var(--warning)' }}
        >
          {t('panelAssign.rematchFailed')}
        </div>
      ) : null}

      {/* Bölüm 1 — Eşleme */}
      <section>
        <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
          {source === 'HAYAT_PANEL'
            ? t('panelAssign.sectionHayatTable')
            : t('panelAssign.sectionAbantTable')}
        </h2>
        {data.isPending ? (
          <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
        ) : data.isError ? (
          <LoadErrorBox onRetry={() => void data.refetch()} />
        ) : labels.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>{t('panelAssign.noLabels')}</p>
        ) : source === 'HAYAT_PANEL' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('panelAssign.hayatHint')}
            </p>
            {labels.map((lbl) => (
              <div
                key={lbl.label}
                data-testid={`mapping-row-${lbl.label}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: 'var(--surface)',
                  borderRadius: 'var(--r-row)',
                  border: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {lbl.label}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {lbl.target ? lbl.target.plate : ''}
                </span>
                <HayatLabelStatus label={lbl} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {labels.map((lbl) => {
              const currentDriverId = lbl.driver?.id ?? null;
              const pendingDriverId =
                pendingMap[lbl.label] !== undefined ? pendingMap[lbl.label] : currentDriverId;
              return (
                <div
                  key={lbl.label}
                  data-testid={`mapping-row-${lbl.label}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 1fr auto auto auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: 'var(--surface)',
                    borderRadius: 'var(--r-row)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <span
                    style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {lbl.label}
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {lbl.displayName ? `${lbl.displayName}` : ''}
                  </span>
                  <select
                    value={pendingDriverId ?? ''}
                    onChange={(e) => handleLabelDriverChange(lbl.label, e.target.value || null)}
                    disabled={!isOwner}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--r-row)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  >
                    <option value="">{t('panelAssign.noMapping')}</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                        {d.vehiclePlate ? ` · ${d.vehiclePlate}` : ''}
                      </option>
                    ))}
                  </select>
                  {/* Bugünkü araç: sunucudan, salt okunur; kaynak/sefer rozetleri gürültüydü, yalnız plaka. */}
                  <div data-testid={`today-vehicle-${lbl.label}`}>
                    {lbl.target ? (
                      <PlateBadge plate={lbl.target.plate} />
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {t('panelAssign.noVehicle')}
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--text-secondary)',
                      minWidth: 40,
                      textAlign: 'right',
                    }}
                  >
                    {lbl.activeCount > 0
                      ? t('panelAssign.pendingCount', { n: lbl.activeCount })
                      : ''}
                  </span>
                </div>
              );
            })}
            {isOwner && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className={`btn ${hasMapChanges ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => void handleSaveMap()}
                  disabled={!hasMapChanges || saveMap.isPending}
                  data-testid="save-map"
                >
                  {saveMap.isPending ? t('common.loading') : t('panelAssign.saveMap')}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Bölüm 2 — Siparişler (salt okunur; atamayı motor yapar) */}
      <section>
        <details>
          <summary style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, cursor: 'pointer' }}>
            {t('panelAssign.ordersCollapsed', { n: orders.length })}
          </summary>
          {data.isPending ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
          ) : data.isError ? (
            <LoadErrorBox onRetry={() => void data.refetch()} />
          ) : orders.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('panelAssign.noOrders')}</p>
          ) : (
            <>
              <p
                style={{
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  margin: '0 0 10px',
                  padding: '8px 12px',
                  background: 'var(--surface)',
                  borderRadius: 'var(--r-row)',
                  border: '1px solid var(--border)',
                }}
              >
                {t('panelAssign.readOnlyNote')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {orders.map((o) => {
                  const { text: timeText, stale } = timeLabel(o.receivedAt);
                  return (
                    <div
                      key={o.orderId}
                      data-testid={`order-row-${o.orderId}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 120px auto',
                        gap: 8,
                        alignItems: 'center',
                        padding: '7px 10px',
                        background: 'var(--surface)',
                        borderRadius: 'var(--r-row)',
                        border: '1px solid var(--border)',
                        opacity: o.reason ? 0.6 : 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontVariantNumeric: 'tabular-nums',
                          color: stale ? 'var(--warning)' : 'var(--text-secondary)',
                        }}
                      >
                        {timeText}
                      </span>
                      <span
                        style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 1 }}
                      >
                        <span style={{ fontWeight: 600 }}>{o.externalId ?? '—'}</span>
                        {o.neighborhood && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                            {o.neighborhood}
                          </span>
                        )}
                      </span>
                      {o.target ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <PlateBadge plate={o.target.plate} />
                        </div>
                      ) : (
                        <span style={{ fontSize: 11.5, color: 'var(--warning)' }}>
                          {o.reason ? reasonLabel(o.reason) : ''}
                        </span>
                      )}
                      <span
                        style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}
                      >
                        {personOf(o.label)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </details>
      </section>
    </div>
  );
}
